import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card, Form, Input, Select, Button, Switch, Space,
  Divider, Alert, notification, Tag, InputNumber,
} from 'antd';
import {
  SendOutlined, EyeOutlined, ArrowLeftOutlined,
  BellOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { TextArea } = Input;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const TYPE_OPTIONS = [
  { value: 'announcement',     label: 'Announcement',           color: 'blue'    },
  { value: 'payment_reminder', label: 'Payment Reminder',       color: 'orange'  },
  { value: 'monthly_reminder', label: 'Monthly Samarpan',       color: 'gold'    },
  { value: 'birthday_system',  label: 'Birthday Wish (ABRSVS)', color: 'magenta' },
];

const SCOPE_OPTIONS = [
  { value: 'all',         label: 'All Members' },
  { value: 'active',      label: 'Active Members Only' },
  { value: 'village',     label: 'By Village' },
  { value: 'tehsil',      label: 'By Tehsil' },
  { value: 'district',    label: 'By District' },
  { value: 'payment_due', label: 'Members with Payment Due' },
  { value: 'birthday',    label: 'Birthday Members (Today)' },
  { value: 'anniversary', label: 'Anniversary Members (Today)' },
];

// common WA templates — admin can also type a custom one
const WA_TEMPLATES = [
  { name: 'abrsvs_announcement',        label: 'General Announcement' },
  { name: 'abrsvs_payment_due',         label: 'Payment Due Reminder' },
  { name: 'abrsvs_monthly_samarpan',    label: 'Monthly Samarpan' },
  { name: 'abrsvs_birthday_wishes',     label: 'Birthday Wishes' },
  { name: 'abrsvs_anniversary_wishes',  label: 'Anniversary Wishes' },
];

export default function AdminNotificationsForm() {
  const history = useHistory();
  const [form]         = Form.useForm();
  const [loading,      setLoading]      = useState(false);
  const [previewing,   setPreviewing]   = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [useWA,        setUseWA]        = useState(false);
  const [scope,        setScope]        = useState('all');
  const [sent,         setSent]         = useState(null);

  const buildPayload = (vals) => {
    const channels = ['portal'];
    if (useWA) channels.push('whatsapp');

    const filter = {};
    if (scope === 'village')      filter.village  = vals.filter_village;
    if (scope === 'tehsil')       filter.tehsil   = vals.filter_tehsil;
    if (scope === 'district')     filter.district = vals.filter_district;
    if (scope === 'payment_due')  filter.months   = vals.filter_months || 3;

    const waParams = vals.wa_params
      ? vals.wa_params.split('\n').map(s => s.trim()).filter(Boolean)
      : [];

    return {
      type:              vals.type,
      title:             vals.title,
      message:           vals.message,
      recipient_scope:   scope,
      recipient_filter:  filter,
      channels,
      wa_template_name:  useWA ? (vals.wa_template_name || null) : null,
      wa_template_params: useWA && waParams.length ? waParams : null,
    };
  };

  const handlePreview = async () => {
    try {
      const vals = await form.validateFields(['type', 'title', 'message']);
      setPreviewing(true);
      setPreviewCount(null);
      const payload = buildPayload({ ...vals, ...form.getFieldsValue() });
      const res = await axios.post(`${baseUrl}/admin/notifications/preview`, {
        scope: payload.recipient_scope,
        filter: payload.recipient_filter,
      }, { headers: authH() });
      if (res.data.status) setPreviewCount(res.data.count);
    } catch (err) {
      if (err?.errorFields) return; // validation error, already shown
      notification.error({ message: 'Preview failed', description: err.response?.data?.message });
    } finally { setPreviewing(false); }
  };

  const handleSend = async (vals) => {
    setLoading(true);
    try {
      const payload = buildPayload({ ...vals, ...form.getFieldsValue() });
      const res = await axios.post(`${baseUrl}/admin/notifications/send`, payload, { headers: authH() });
      if (res.data.status) {
        setSent(res.data);
        notification.success({
          message: 'Notification Sent!',
          description: `Portal: ${res.data.portal_sent} | WhatsApp: ${res.data.wa_sent || 0}`,
        });
        form.resetFields();
        setPreviewCount(null);
        setUseWA(false);
        setScope('all');
      }
    } catch (err) {
      notification.error({ message: 'Send failed', description: err.response?.data?.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="layout-content">
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} size="small" onClick={() => history.push('/admin-notifications')} />
            <BellOutlined style={{ color: '#7a0000' }} />
            <span style={{ color: '#7a0000', fontWeight: 700 }}>New Notification</span>
          </Space>
        }
        headStyle={{ background: '#f8fafc', borderBottom: '2px solid #7a0000' }}
        style={{ maxWidth: 720, margin: '0 auto' }}
      >
        {sent && (
          <Alert type="success" showIcon closable onClose={() => setSent(null)}
            style={{ marginBottom: 20 }}
            message={`Sent to ${sent.portal_sent} members (Portal) + ${sent.wa_sent || 0} (WhatsApp)`}
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleSend}
          initialValues={{ type: 'announcement', filter_months: 3 }}>

          {/* Type */}
          <Form.Item label="Notification Type" name="type" rules={[{ required: true }]}>
            <Select>
              {TYPE_OPTIONS.map(o => (
                <Select.Option key={o.value} value={o.value}>
                  <Tag color={o.color}>{o.label}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Title + Message */}
          <Form.Item label="Title (short headline)" name="title"
            rules={[{ required: true, message: 'Title required' }, { max: 255 }]}>
            <Input placeholder="e.g. मासिक समर्पण — अगस्त 2026" />
          </Form.Item>

          <Form.Item label="Message" name="message"
            rules={[{ required: true, message: 'Message required' }]}>
            <TextArea rows={4} placeholder="पूरा संदेश यहाँ लिखें…" showCount maxLength={1000} />
          </Form.Item>

          <Divider orientation="left" style={{ fontSize: 13, color: '#6b7280' }}>Recipients</Divider>

          {/* Scope */}
          <Form.Item label="Send To" name="scope">
            <Select value={scope} onChange={v => { setScope(v); setPreviewCount(null); }}
              defaultValue="all">
              {SCOPE_OPTIONS.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          {scope === 'village' && (
            <Form.Item label="Village Name" name="filter_village"
              rules={[{ required: true, message: 'Enter village name' }]}
              extra="गाँव का नाम टाइप करें (Hindi या English — partial match होगा)">
              <Input placeholder="e.g. Biti / बिती" allowClear />
            </Form.Item>
          )}
          {scope === 'tehsil' && (
            <Form.Item label="Tehsil Name" name="filter_tehsil"
              rules={[{ required: true, message: 'Enter tehsil name' }]}
              extra="तहसील का नाम टाइप करें (Hindi या English — partial match होगा)">
              <Input placeholder="e.g. Bali / बाली" allowClear />
            </Form.Item>
          )}
          {scope === 'district' && (
            <Form.Item label="District Name" name="filter_district"
              rules={[{ required: true, message: 'Enter district name' }]}
              extra="जिले का नाम टाइप करें (Hindi या English — partial match होगा)">
              <Input placeholder="e.g. Pali / पाली" allowClear />
            </Form.Item>
          )}
          {scope === 'payment_due' && (
            <Form.Item label="Months without payment" name="filter_months">
              <InputNumber min={1} max={24} style={{ width: 120 }} addonAfter="months" />
            </Form.Item>
          )}

          {previewCount !== null && (
            <Alert type="info" showIcon style={{ marginBottom: 16 }}
              message={`${previewCount} member${previewCount !== 1 ? 's' : ''} will receive this notification`} />
          )}

          <Divider orientation="left" style={{ fontSize: 13, color: '#6b7280' }}>Channels</Divider>

          <Form.Item>
            <Space align="center">
              <Switch checked={useWA} onChange={v => setUseWA(v)} />
              <span style={{ fontWeight: 600, color: '#075e54' }}>
                <WhatsAppOutlined style={{ marginRight: 4 }} />
                Also send via WhatsApp
              </span>
            </Space>
          </Form.Item>

          {useWA && (
            <>
              <Form.Item label="WhatsApp Template Name" name="wa_template_name"
                rules={[{ required: useWA, message: 'Template name required for WhatsApp' }]}>
                <Select showSearch allowClear
                  placeholder="Select or type a template name"
                  dropdownRender={menu => <>{menu}</>}>
                  {WA_TEMPLATES.map(t => (
                    <Select.Option key={t.name} value={t.name}>{t.name} — {t.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Template Params (one per line)" name="wa_params"
                help="Use {name} as placeholder for member's name. Leave blank if template has no params.">
                <TextArea rows={3} placeholder="Param 1&#10;Param 2&#10;{name}" />
              </Form.Item>
            </>
          )}

          <Divider />

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button icon={<EyeOutlined />} onClick={handlePreview} loading={previewing}>
              Preview Recipients
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}
              icon={<SendOutlined />}
              style={{ background: '#7a0000', borderColor: '#7a0000' }}>
              Send Notification
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}
